import { Component, ErrorInfo, ReactNode } from 'react';

import { t } from 'i18next';

import { addToast } from '@/app/slices/toast';
import { store } from '@/app/store';
import { ToastLife } from '@/components/hoc/toast-container/enums';

import { ErrorFallback } from './ErrorFallback';

type ErrorBoundaryProps = {
  children: ReactNode;
  fallback?: (args: { error: Error; reset: () => void }) => ReactNode;
  onReset?: () => void;
};

type ErrorBoundaryState = {
  error: Error | null;
};

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('Uncaught error:', error, errorInfo);
    store.dispatch(
      addToast({
        severity: 'error',
        summary: t('common.anErrorOccurred'),
        detail: t('common.somethingWentWrongNotifyHelpDesk'),
        life: ToastLife.LONG,
      }),
    );
  }

  reset = (): void => {
    this.props.onReset?.();
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    if (error) {
      if (this.props.fallback) {
        return this.props.fallback({ error, reset: this.reset });
      }
      return <ErrorFallback error={error} reset={this.reset} />;
    }

    return this.props.children;
  }
}
